import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import { AnimatePresence, motion, useMotionValue } from "framer-motion";
import {
  IoMdClose,
  IoMdCheckmarkCircle,
  IoMdAlert,
  IoMdInformationCircle,
} from "react-icons/io";

type TransitType = "success" | "error" | "loading" | "info";

interface TransitOptions {
  duration?: number | null;
  sticky?: boolean;
}

interface TransitToast {
  id: string;
  type: TransitType;
  title: string;
  message?: ReactNode;
  duration: number | null;
  sticky: boolean;
}

interface TransitDialogContextType {
  show: (
    type: TransitType,
    title: string,
    message?: ReactNode,
    options?: TransitOptions
  ) => void;
  close: (id: string) => void;
}

export const TransitDialogContext =
  createContext<TransitDialogContextType | undefined>(undefined);

const DEFAULT_DURATION = 2800;
const MAX_STACK = 4;

export const TransitDialogProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [toasts, setToasts] = useState<TransitToast[]>([]);

  /* ---------- SHOW ---------- */
  const show = useCallback(
    (
      type: TransitType,
      title: string,
      message?: ReactNode,
      options?: TransitOptions
    ) => {
      const toast: TransitToast = {
        id: crypto.randomUUID(),
        type,
        title,
        message,
        duration: options?.duration ?? DEFAULT_DURATION,
        sticky: options?.sticky ?? false,
      };

      setToasts((prev) => [...prev.slice(-MAX_STACK + 1), toast]);
    },
    []
  );

  /* ---------- CLOSE ---------- */
  const close = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <TransitDialogContext.Provider value={{ show, close }}>
      {children}

      {/* ---------- STACK ---------- */}
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] w-full max-w-md px-4">
        <AnimatePresence>
          {toasts.map((toast) => (
            <ToastItem key={toast.id} toast={toast} onClose={close} />
          ))}
        </AnimatePresence>
      </div>
    </TransitDialogContext.Provider>
  );
};

/* ---------- TOAST ITEM ---------- */
const ToastItem = ({
  toast,
  onClose,
}: {
  toast: TransitToast;
  onClose: (id: string) => void;
}) => {
  const y = useMotionValue(0);

  const icon =
    toast.type === "success" ? (
      <IoMdCheckmarkCircle className="text-green-500 text-xl" />
    ) : toast.type === "error" ? (
      <IoMdAlert className="text-red-500 text-xl" />
    ) : toast.type === "info" ? (
      <IoMdInformationCircle className="text-blue-500 text-xl" />
    ) : (
      <span className="h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
    );

  return (
    <motion.div
      layout
      drag="y"
      dragConstraints={{ top: 0, bottom: 80 }}
      style={{ y }}
      onDragEnd={(_, info) => {
        if (info.offset.y > 60 || info.velocity.y > 500) {
          onClose(toast.id);
        }
      }}
      initial={{ opacity: 0, y: 30, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ type: "spring", damping: 22 }}
      className="relative mb-3 bg-white dark:bg-gray-800 rounded-xl shadow-lg px-4 py-3"
    >
      {/* Close */}
      <button
        onClick={() => onClose(toast.id)}
        className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
      >
        <IoMdClose />
      </button>

      <div className="flex gap-3 items-start">
        {icon}
        <div className="flex-1">
          <div className="font-semibold">{toast.title}</div>
          {toast.message && (
            <div className="text-sm text-gray-600 dark:text-gray-300">
              {toast.message}
            </div>
          )}
        </div>
      </div>

      {/* ⏳ PROGRESS BAR */}
      {toast.duration && (
        <motion.div
          className="absolute left-0 bottom-0 h-[3px] bg-blue-500 rounded-b-xl"
          initial={{ width: "100%" }}
          animate={{ width: 0 }}
          transition={{ duration: toast.duration / 1000, ease: "linear" }}
          onAnimationComplete={() => onClose(toast.id)}
        />
      )}
    </motion.div>
  );
};

/* ---------- HOOK ---------- */
export function useTransitDialog() {
  const ctx = useContext(TransitDialogContext);

  if (!ctx) {
    throw new Error("useTransitDialog must be used inside TransitDialogProvider");
  }

  return {
    showSuccess: (
      title: string,
      message?: ReactNode,
      options?: { duration?: number | null; sticky?: boolean }
    ) => ctx.show("success", title, message, options),

    showError: (
      title: string,
      message?: ReactNode,
      options?: { duration?: number | null; sticky?: boolean }
    ) => ctx.show("error", title, message, options),

    showInfo: (
      title: string,
      message?: ReactNode,
      options?: { duration?: number | null; sticky?: boolean }
    ) => ctx.show("info", title, message, options),

    showLoading: (
      title = "Processing…",
      message?: ReactNode,
      options?: { sticky?: boolean }
    ) =>
      ctx.show("loading", title, message, {
        duration: null,
        sticky: true,
        ...options,
      }),

    close: ctx.close,
  };
}